import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PatientFilterOptions,
  ReportFilterOptions,
  StatusFilterOptions
} from '@/pages/physician/PatientsPage/constants';
import {
  PatientOptionsFilter,
  ReportOptionsFilter,
  StatusFilter
} from '@/pages/physician/PatientsPage/PatientList';
import { LabelAndValue } from '@/types/custom';

import { Patient, PatientsResponse } from '../../../types/patient';

export interface PatientFilters {
  patientOptions: PatientOptionsFilter;
  reportOptions: ReportOptionsFilter;
  status: StatusFilter;
  search: string | undefined;
  deviceSchedule: LabelAndValue | undefined;
  clinics: LabelAndValue[] | undefined;
  practitioners: LabelAndValue[] | undefined;
  devices: LabelAndValue[] | undefined;
  vendors: LabelAndValue[] | undefined;
}

interface PatientState {
  patients: Patient[];
  total: number;
  totalNotDownloadedCount: string;
  currentOffset: number;
  isLoading: boolean;
  error: string | null;
  checkedPatients?: CheckedPatients[] | null;
  isAllPatientsChecked?: boolean;
  isCurrentPageAllPatientsChecked?: boolean;
  page: number;
  pageLimit: number;
  skip: number;
  filters: PatientFilters;
}

export interface CheckedPatients {
  patientId: string;
  transmissionData: Array<TransmissionData>;
}

export interface TransmissionData {
  transmissionId?: number;
}

const initialState: PatientState = {
  patients: [],
  total: 0,
  totalNotDownloadedCount: '0',
  currentOffset: 0,
  isLoading: false,
  error: null,
  checkedPatients: null,
  page: 1,
  pageLimit: 10,
  skip: 0,
  isAllPatientsChecked: false,
  isCurrentPageAllPatientsChecked: false,
  filters: {
    patientOptions: PatientFilterOptions[0] as PatientOptionsFilter,
    reportOptions: ReportFilterOptions[0] as ReportOptionsFilter,
    status: StatusFilterOptions[0] as StatusFilter,
    search: undefined as string | undefined,
    deviceSchedule: undefined as LabelAndValue | undefined,
    clinics: [] as LabelAndValue[] | undefined,
    practitioners: [] as LabelAndValue[] | undefined,
    devices: [] as LabelAndValue[] | undefined,
    vendors: [] as LabelAndValue[] | undefined
  }
};

export const patientsSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    setPatients: (state, action: PayloadAction<PatientsResponse>) => {
      state.patients = action.payload.patients;
      state.total = action.payload.total;
      state.totalNotDownloadedCount = action.payload.totalNotDownloadedCount;
      state.currentOffset = action.payload.currentOffset;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCheckedPatients: (state, action: PayloadAction<CheckedPatients[]>) => {
      state.checkedPatients = action.payload;
    },
    setIsAllPatientsChecked: (state, action: PayloadAction<boolean>) => {
      state.isAllPatientsChecked = action.payload;
    },
    setIsCurrentPageAllPatientsChecked: (state, action: PayloadAction<boolean>) => {
      state.isCurrentPageAllPatientsChecked = action.payload;
    },
    setFilters: (state, action: PayloadAction<PatientFilters>) => {
      state.filters = action.payload;
      state.page = 1; // Reset page to 1 when filters change
      state.skip = 0; // Reset skip when filters change
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
      state.skip = (state.page - 1) * state.pageLimit;
    },
    setPageLimit: (state, action: PayloadAction<number>) => {
      state.pageLimit = action.payload;
      state.page = 1;
      state.skip = 0;
    }
  }
});

export const {
  setPatients,
  setLoading,
  setError,
  setCheckedPatients,
  setIsAllPatientsChecked,
  setIsCurrentPageAllPatientsChecked,
  setFilters,
  setPage,
  setPageLimit
} = patientsSlice.actions;

export default patientsSlice.reducer;
